import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";
import { toast } from "react-toastify";
import Select from "react-select";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncInProgressClientSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { phoneNumberShowFormat } from "helpers/GeneralHelper";
import {
    fetchReportMailSourceOptions,
    mapReportMailSourceOptions,
    submitInProgressReportFilterData,
    submitInProgressReportSendData,
} from "model/InProgressReportModel";

const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const TABLE_COLUMNS = [
    { Header: "Name", accessor: "name" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Email", accessor: "email" },
    { Header: "Position", accessor: "position" },
    { Header: "Status", accessor: "status", statusColClass: "common-table-status-col--wide" },
    { Header: "Notes", accessor: "notes" },
];

const formatOrderStatusLabel = (value = "") => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const normalized = raw.toLowerCase();
    if (normalized === "in progress") return "In Progress";
    if (normalized === "eligible") return "Eligible";
    return raw
        .split(/\s+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(nestedData?.records)
                ? nestedData.records
                : Array.isArray(responseData?.records)
                    ? responseData.records
                    : Array.isArray(nestedData?.data)
                        ? nestedData.data
                        : Array.isArray(responseData?.data)
                            ? responseData.data
                            : Array.isArray(responseData)
                                ? responseData
                                : [];

    return {
        rows,
        clientId: nestedData?.clientId ?? responseData?.clientId ?? "",
        mailSourceId: nestedData?.mailSourceId ?? responseData?.mailSourceId ?? "",
    };
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const orderStatus = item?.orderStatus ?? item?.status ?? "";
        const statusLabel = formatOrderStatusLabel(orderStatus);

        const specialNotes = String(item?.specialNotes ?? item?.specialNote ?? "").trim();

        return {
            ...item,
            id: item?.candidateClientId ?? item?.id ?? item?._id ?? `row-${index}`,
            candidateClientId: item?.candidateClientId ?? "",
            name: item?.candidateFullName ?? item?.name ?? "",
            specialNotes,
            hasSpecialNote: Boolean(
                item?.hasSpecialNote ?? item?.isSpecialNote ?? specialNotes
            ),
            phone: phoneNumberShowFormat(item?.candidatePhone ?? item?.phone ?? ""),
            email: item?.candidateEmail ?? item?.email ?? "",
            position: item?.jobName ?? item?.position ?? "",
            status: statusLabel,
            statusLabel,
            orderStatus,
            isMecComplete: Boolean(item?.isMecComplete),
            notes: item?.notes ?? "",
        };
    });

const InProgressReport = () => {
    const [filterForm, setFilterForm] = useState({
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mailSourceOptions, setMailSourceOptions] = useState([]);
    const [emailFrom, setEmailFrom] = useState(null);
    const [preferredMailSourceId, setPreferredMailSourceId] = useState("");
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [sendingMail, setSendingMail] = useState(false);

    useEffect(() => {
        const loadMailSourceOptions = async () => {
            const response = await fetchReportMailSourceOptions();
            if (response == null || response?.success === false) {
                setMailSourceOptions([]);
                return;
            }
            setMailSourceOptions(mapReportMailSourceOptions(response));
        };
        loadMailSourceOptions();
    }, []);

    useEffect(() => {
        if (!mailSourceOptions.length) {
            setEmailFrom(null);
            return;
        }
        const preferredId = strOrNull(preferredMailSourceId);
        const matchedOption = preferredId
            ? mailSourceOptions.find((option) => String(option.value) === preferredId)
            : null;
        setEmailFrom(matchedOption ?? mailSourceOptions[0]);
    }, [mailSourceOptions, preferredMailSourceId]);

    const filteredData = useMemo(() => reportData, [reportData]);

    const resolveRowCandidateClientId = useCallback(
        (row = {}) =>
            String(row?.candidateClientId ?? row?.id ?? "").trim(),
        []
    );

    const handleSelectionChange = useCallback((selectedIds = []) => {
        setSelectedCandidateIds(
            Array.isArray(selectedIds)
                ? selectedIds.map((id) => String(id).trim()).filter(Boolean)
                : []
        );
    }, []);

    const handleNotesBlur = useCallback((row = {}, notesValue = "") => {
        const rowId = resolveRowCandidateClientId(row);
        if (!rowId) return;
        setReportData((previousRows) =>
            previousRows.map((item) =>
                resolveRowCandidateClientId(item) === rowId
                    ? { ...item, notes: String(notesValue ?? "") }
                    : item
            )
        );
    }, [resolveRowCandidateClientId]);

    const buildFilterPayload = (clientId = filterForm?.clientId) => ({
        clientId: strOrNull(clientId),
    });

    const getInProgressReportList = async (clientId = filterForm?.clientId) => {
        const resolvedClientId = strOrNull(clientId);
        if (!resolvedClientId) {
            toast.error("Please select a client");
            setReportData([]);
            return;
        }

        setLoading(true);
        setSelectedCandidateIds([]);
        try {
            const response = await submitInProgressReportFilterData(
                buildFilterPayload(resolvedClientId)
            );
            const { rows, mailSourceId: responseMailSourceId } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setPreferredMailSourceId("");
                setSelectedCandidateIds([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setPreferredMailSourceId(strOrNull(responseMailSourceId) || "");
            setReportData(mapRowsFromApi(rows));
        } catch (error) {
            toast.error(error?.message || "Unable to load in progress report");
            setReportData([]);
            setPreferredMailSourceId("");
            setSelectedCandidateIds([]);
        } finally {
            setLoading(false);
        }
    };

    const buildSendMailPayload = () => {
        const selectedIdSet = new Set(selectedCandidateIds);
        return {
            clientId: strOrNull(filterForm?.clientId),
            mailSourceId: strOrNull(emailFrom?.value),
            items: reportData.map((row) => {
                const candidateClientId = resolveRowCandidateClientId(row);
                return {
                    candidateClientId,
                    isSelected: selectedIdSet.has(candidateClientId),
                    notes: String(row?.notes ?? ""),
                };
            }),
        };
    };

    const handleSendMail = async () => {
        const clientId = strOrNull(filterForm?.clientId);
        const mailSourceId = strOrNull(emailFrom?.value);

        if (!clientId) {
            toast.error("Please select a client");
            return;
        }
        if (!mailSourceId) {
            toast.error("Mail source is required");
            return;
        }
        if (!reportData.length) {
            toast.error("No candidates available to send");
            return;
        }
        if (!selectedCandidateIds.length) {
            toast.error("Please select at least one candidate");
            return;
        }

        setSendingMail(true);
        try {
            const response = await submitInProgressReportSendData(buildSendMailPayload());

            if (response == null || response?.success === false) {
                toast.error(response?.message || "Unable to send mail");
                return;
            }

            toast.success(response?.message || "Mail sent successfully");
            setSelectedCandidateIds([]);
            await getInProgressReportList(clientId);
        } catch (error) {
            toast.error(error?.message || "Unable to send mail");
        } finally {
            setSendingMail(false);
        }
    };

    const handleClientChange = (option) => {
        const nextForm = applyClientAsyncFilterOptionToFormState(filterForm, option);
        setFilterForm(nextForm);

        if (nextForm?.clientId) {
            getInProgressReportList(nextForm.clientId);
        } else {
            setReportData([]);
            setPreferredMailSourceId("");
            setSelectedCandidateIds([]);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content mail-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Inprogress Report</h5>
                            <hr />

                            <div className="upper-filter-bar manage-user-filter-strip mail-report-toolbar">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={6}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncInProgressClientSelect
                                            key={`inprogress-report-client-${filterForm?.clientId || ""}`}
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={handleClientChange}
                                            placeholder="Select Client"
                                            className="select2-selection w-100"
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={6}>
                                        <span className="manage-user-filter-label">Mail</span>
                                        <Select
                                            className="select2-selection"
                                            options={mailSourceOptions}
                                            value={emailFrom}
                                            onChange={setEmailFrom}
                                            placeholder="Select mail source"
                                            isSearchable={false}
                                            isDisabled={!mailSourceOptions.length}
                                            menuPortalTarget={
                                                typeof document !== "undefined" ? document.body : null
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <Row className="align-items-center mb-3">
                                <Col className="text-end">
                                    <Button
                                        color="primary"
                                        type="button"
                                        disabled={sendingMail || loading || !reportData.length}
                                        onClick={handleSendMail}
                                    >
                                        <i className="mdi mdi-email-outline me-1" aria-hidden="true" />
                                        {sendingMail ? "Sending…" : "Send Mail"}
                                    </Button>
                                </Col>
                            </Row>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={TABLE_COLUMNS}
                                    data={filteredData}
                                    isActionRequired={false}
                                    enableRowSelection
                                    isCheckboxLeftSide
                                    pageName="InProgressReport"
                                    resolveRowId={resolveRowCandidateClientId}
                                    onSelectionChange={handleSelectionChange}
                                    onNotesBlur={handleNotesBlur}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

InProgressReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(InProgressReport);
